import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Instagram } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/playmatez_sportsapp_india?igsh=MWZteWZrd2xjb2hrZQ==', label: 'Instagram' },
  ];

  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'Features', href: '/features' },
    { name: 'Partner with Us', href: '/partner' },
    { name: 'Shop', href: '/shop' },
    { name: 'Support Hub', href: '/support' },
    { name: 'Contact', href: '/contact' },
  ];

  const legalLinks = [
    { name: 'Privacy Policy', href: '/privacy-policy' },
    { name: 'Terms & Conditions', href: '/terms' },
  ];

  return (
    <footer className="relative border-t border-white/10 bg-card/50" data-testid="footer">
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_play-connect-in/artifacts/zk1iohyx_image.png" 
                alt="Playmatez Logo" 
                className="h-10 w-auto"
              />
            </Link>
            <p className="text-muted-foreground mb-6 text-sm">
              Find. Connect. Play. Grow. India's premier sports networking app for players, 
              crews, clubs, and venues.
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    className="w-9 h-9 rounded-full bg-muted/50 flex items-center justify-center text-muted-foreground hover:bg-primary hover:text-white transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label={social.label}
                    data-testid={`social-${social.label.toLowerCase()}`}
                  >
                    <Icon size={16} />
                  </motion.a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-bold text-white mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-muted-foreground hover:text-white transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Download */}
          <div>
            <h4 className="font-heading font-bold text-white mb-6">Download App</h4>
            <div className="space-y-3">
              <a
                href="https://play.google.com/store/apps/details?id=com.app.playmatez&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
                className="block hover:opacity-80 transition-opacity"
                data-testid="footer-google-play"
              >
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" 
                  alt="Get it on Google Play" 
                  className="h-12"
                />
              </a>
              <a
                href="https://apps.apple.com/app/playmatez/id6450857903"
                target="_blank"
                rel="noopener noreferrer"
                className="block hover:opacity-80 transition-opacity"
                data-testid="footer-app-store"
              >
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" 
                  alt="Download on the App Store" 
                  className="h-12"
                />
              </a>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading font-bold text-white mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground text-sm">
                  7-54/13/2, Balasaraswathi Nagar, Malkajgiri, Hyderabad, Telangana, 500047
                </span>
              </li>
              <li>
                <a
                  href="mailto:connect@playmatez.io"
                  className="flex items-center gap-3 text-muted-foreground hover:text-white transition-colors text-sm"
                  data-testid="email-link"
                >
                  <Mail className="w-5 h-5 text-primary flex-shrink-0" />
                  connect@playmatez.io
                </a>
              </li>
            </ul>
            {/* Static Map - OpenStreetMap Style */}
            <div className="mt-4 rounded-xl overflow-hidden border border-white/10">
              <a 
                href="https://www.google.com/maps/search/?api=1&query=17.4500,78.5200"
                target="_blank"
                rel="noopener noreferrer"
                className="block relative group"
              >
                <img 
                  src="https://www.openstreetmap.org/export/embed.html?bbox=78.51%2C17.44%2C78.53%2C17.46&layer=mapnik&marker=17.45%2C78.52"
                  alt="Playmatez India Office - Malkajgiri, Hyderabad"
                  className="w-full h-28 object-cover hidden"
                />
                <div className="w-full h-28 bg-[#1a1a2e] relative overflow-hidden">
                  {/* Custom Map Styling */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a2e] to-[#16213e]">
                    {/* Grid lines to simulate map */}
                    <div className="absolute inset-0 opacity-20">
                      <div className="grid grid-cols-6 grid-rows-4 h-full">
                        {[...Array(24)].map((_, i) => (
                          <div key={i} className="border border-white/10"></div>
                        ))}
                      </div>
                    </div>
                    {/* Road lines */}
                    <div className="absolute top-1/2 left-0 right-0 h-[2px] bg-white/20"></div>
                    <div className="absolute top-0 bottom-0 left-1/3 w-[2px] bg-white/20"></div>
                    <div className="absolute top-0 bottom-0 right-1/4 w-[2px] bg-white/15"></div>
                    {/* Location marker */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full">
                      <div className="relative">
                        <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center shadow-lg shadow-primary/50">
                          <MapPin className="w-5 h-5 text-white" />
                        </div>
                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-primary rotate-45"></div>
                      </div>
                    </div>
                    {/* Location label */}
                    <div className="absolute bottom-2 left-2 right-2">
                      <p className="text-[10px] text-white/60 truncate">Malkajgiri, Hyderabad</p>
                    </div>
                  </div>
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-medium bg-primary/90 px-3 py-1 rounded-full">
                    Open in Maps
                  </span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container-custom py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-muted-foreground text-sm">
            © {currentYear} Playmatez India Pvt. Ltd. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            {legalLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="text-muted-foreground hover:text-white text-sm transition-colors"
                data-testid={`footer-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
